import { Fragment } from "react";
import {
  netural400,
  neturalBlack,
  success700,
  errorRed,
  primaryOrange,
} from "../../constants/color";

/**
 * TransferInfo Component - Matches Mobile App Design
 * Displays seller payout/transfer information with status-specific styling
 *
 * @param {Object} transferInfo - Transfer information object
 * @param {string} transferInfo.status - Transfer status: 'pending', 'scheduled', 'in_transit', 'completed', 'paid', 'failed'
 * @param {number} transferInfo.transferAmount - Amount to be transferred
 * @param {string} transferInfo.releaseDate - Expected release/transfer date
 * @param {string} transferInfo.transferredAt - Actual transfer completion date (if completed)
 * @param {string} transferInfo.lastError - Error message if transfer failed
 */
const TransferInfo = ({ transferInfo }) => {
  // Don't render if no transfer info available
  if (!transferInfo) {
    return null;
  }

  const formatDate = (inputDate) => {
    if (!inputDate) return null;
    const options = {
      month: "short",
      day: "2-digit",
      year: "numeric",
    };
    const date = new Date(inputDate);
    return date.toLocaleDateString("en-US", options);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "$ 0.00";
    return `$ ${parseFloat(amount).toFixed(2)}`;
  };

  // Status configuration for different transfer states
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "transferred":
      case "paid":
        return {
          label: "Transferred",
          backgroundColor: "#E6F6E6", // Light green
          statusColor: success700,
          icon: "✓",
        };
      case "in_transit":
      case "processing":
        return {
          label: "In Transit",
          backgroundColor: "#E6F1FD", // Light blue
          statusColor: primaryOrange,
          icon: "⇄",
        };
      case "failed":
      case "error":
        return {
          label: "Failed",
          backgroundColor: "#FDE7E7", // Light red/pink
          statusColor: errorRed,
          icon: "!",
        };
      case "scheduled":
      case "pending":
      default:
        return {
          label: "Scheduled",
          backgroundColor: "#E6F1FD", // Light blue
          statusColor: primaryOrange,
          icon: "⏱",
        };
    }
  };

  const statusConfig = getStatusConfig(transferInfo.status);
  const isCompleted =
    transferInfo.status?.toLowerCase() === "completed" ||
    transferInfo.status?.toLowerCase() === "transferred" ||
    transferInfo.status?.toLowerCase() === "paid";
  const isFailed =
    transferInfo.status?.toLowerCase() === "failed" ||
    transferInfo.status?.toLowerCase() === "error";

  return (
    <div
      style={{
        marginTop: "5%",
        paddingBottom: "4%",
        borderBottom: "1px solid #dadada",
      }}
    >
      <Fragment>
        <label
          style={{
            fontSize: "20px",
            fontWeight: 600,
            color: neturalBlack,
          }}
        >
          Transfer details
        </label>
      </Fragment>

      {/* Main Status Card */}
      <div
        style={{
          marginTop: "3%",
          padding: "16px",
          backgroundColor: statusConfig.backgroundColor,
          borderRadius: "12px",
        }}
      >
        {/* Status Row with Icon */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "12px",
          }}
        >
          {/* Status Icon */}
          <div
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              backgroundColor:
                isCompleted ? success700 : isFailed ? errorRed : "transparent",
              border:
                isCompleted || isFailed
                  ? "none"
                  : `2px solid ${statusConfig.statusColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color:
                isCompleted || isFailed ? "white" : statusConfig.statusColor,
              fontWeight: "bold",
              fontSize: isCompleted ? "12px" : "14px",
            }}
          >
            {statusConfig.icon}
          </div>

          {/* Status Label */}
          <label
            style={{
              fontSize: "16px",
              fontWeight: 600,
              color: statusConfig.statusColor,
            }}
          >
            {statusConfig.label}
          </label>
        </div>

        {/* Amount Row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <label
            style={{
              fontSize: "15px",
              color: netural400,
            }}
          >
            Amount
          </label>
          <label
            style={{
              fontSize: "15px",
              fontWeight: 500,
              color: neturalBlack,
            }}
          >
            {formatCurrency(transferInfo.transferAmount)}
          </label>
        </div>

        {/* Expected By Row */}
        {formatDate(transferInfo.releaseDate) && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom:
                isCompleted && transferInfo.transferredAt ? "8px" : "0",
            }}
          >
            <label
              style={{
                fontSize: "15px",
                color: netural400,
              }}
            >
              Expected by
            </label>
            <label
              style={{
                fontSize: "15px",
                color: netural400,
              }}
            >
              {formatDate(transferInfo.releaseDate)}
            </label>
          </div>
        )}

        {/* Transferred On Row (only for completed) */}
        {isCompleted && transferInfo.transferredAt && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <label
              style={{
                fontSize: "15px",
                color: netural400,
              }}
            >
              Transferred on
            </label>
            <label
              style={{
                fontSize: "15px",
                fontWeight: 500,
                color: success700,
              }}
            >
              {formatDate(transferInfo.transferredAt)}
            </label>
          </div>
        )}
      </div>

      {/* Error Message Box (only for failed status) */}
      {isFailed && transferInfo.lastError && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px 16px",
            backgroundColor: "#FDE7E7",
            borderRadius: "8px",
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
          }}
        >
          {/* Warning Icon */}
          <span
            style={{
              color: errorRed,
              fontSize: "14px",
              marginTop: "2px",
            }}
          >
            ⚠
          </span>
          {/* Error Text */}
          <label
            style={{
              fontSize: "14px",
              color: errorRed,
              lineHeight: "20px",
              flex: 1,
            }}
          >
            {transferInfo.lastError}
          </label>
        </div>
      )}
    </div>
  );
};

export default TransferInfo;
